import type { District } from '../plot.ts'
import { decals, look, woodsy } from '../kit.ts'

/** The Harbour: a boardwalk, a pier into the sea, fishing boats and the lighthouse. */
export const harbour: District = {
  id: 'harbour',
  name: 'The Harbour',
  ground: 'grass',
  ambience: ['gulls'],
  build(p) {
    p.zone(0, 17, 36, 3, 'planks')
    p.zone(0, 20, 36, 12, 'sea')
    p.zone(14, 20, 3, 9, 'planks')
    p.zone(27, 11, 8, 6, 'stone')
    p.prop(
      { kind: 'building', x: 30, y: 12, w: 3, d: 3, style: 'lighthouse', h: 7, color: '#ffffff', roof: '#d94a5a' },
      { kind: 'building', x: 3, y: 11, w: 5, d: 4, door: 2, style: 'shack', color: '#8fb8d6', roof: '#3a4a5a', text: 'Fresh Fish' },
      { kind: 'building', x: 10, y: 11, w: 4, d: 4, door: 2, style: 'shack', color: '#e8d7b8', roof: '#7a5a3a' },
      { kind: 'boat', x: 9, y: 22 }, { kind: 'boat', x: 20, y: 23 }, { kind: 'boat', x: 24, y: 21 },
      { kind: 'campfire', x: 20, y: 8 },
      { kind: 'stump', x: 19, y: 8, face: 1 }, { kind: 'stump', x: 21, y: 8, face: -1 }, { kind: 'stump', x: 20, y: 7, face: 1 }, { kind: 'stump', x: 20, y: 9, face: -1 },
      { kind: 'palm', x: 8, y: 5 }, { kind: 'palm', x: 25, y: 4 }, { kind: 'palm', x: 13, y: 3 },
      { kind: 'bench', x: 14, y: 27, face: 1 },
      { kind: 'lanternpost', x: 16, y: 21 }, { kind: 'lanternpost', x: 14, y: 24 },
      { kind: 'signpost', x: 12, y: 16, text: 'Harbour' },
    )
    p.tram(23, 14)
    p.place({ id: 'harbour', name: 'The Harbour', blurb: 'A pier into the sea, fishing boats, and the lighthouse.', x: 15, y: 18, accent: '#4f9cc4', r: 10 })
    p.place({ id: 'pier', name: 'End of the Pier', blurb: 'Nothing but sea from here.', x: 15, y: 26, accent: '#6fb7d8', r: 2 })
    p.scatter({ density: 0.06, pick: woodsy([['tree', 3], ['bush', 2], ['palm', 2]]), on: ['grass'] })
    p.critter({ kind: 'crab', loop: [[2.5, 18.5], [10, 18.6], [22.5, 18.4], [12, 18.5]], speed: 0.5 })
    p.critter({ kind: 'boat', loop: [[2, 25], [30, 27], [33, 23], [5, 22]], speed: 0.35 })
    p.npc({
      id: 'npc-captain', name: 'Captain Kelp', role: 'Storyteller', look: look('captkelp', 7, 11, 3, 'viking'), speed: 1,
      stops: [{ x: 19, y: 8, wait: 45, emote: 'thinking', say: -1 }, { x: 23, y: 10, wait: 8, say: -1 }, { x: 20, y: 9, wait: 40, nap: true }],
      lines: ['Did I ever tell you about the giant jellyfish?', 'Pull up a stump, friend.', 'The sea is calm tonight.', 'Arr. I mean… hello.'],
    })
    p.npc({
      id: 'npc-finn', name: 'Fisher Finn', role: 'Fisher', look: look('fisherfinn', 4, 13, 2, 'beanie'), speed: 1.2,
      stops: [{ x: 14, y: 27, wait: 60, emote: 'thinking', say: -1 }, { x: 5, y: 16, wait: 10, say: -1 }, { x: 15, y: 27, wait: 40, nap: true }],
      lines: ['Nothing biting yet.', 'Caught a boot once. Very stylish.', 'Freshest fish in Blobland, right there.', 'Shh, you’ll scare them.'],
    })
    p.npc({
      id: 'npc-gale', name: 'Keeper Gale', role: 'Lighthouse keeper', look: look('keepergale', 3, 5, 0, 'cap'), speed: 1,
      stops: [{ x: 29, y: 16, wait: 20, say: -1 }, { x: 34, y: 18, wait: 12, emote: 'happy' }, { x: 27, y: 12, wait: 15, say: -1 }],
      lines: ['The light comes on at dusk. Every dusk.', 'Ships? Mostly ducks.', 'You can see the canals from up top.', 'Mind the steps.'],
    })

    // ─── Detail ───
    p.prop(
      ...[1, 5, 9, 20, 24, 28, 33].map((x) => ({ kind: 'bollard' as const, x, y: 19 })),
      { kind: 'stall', x: 6, y: 16, color: '#4f9cc4', text: 'Fish' }, { kind: 'crate', x: 9, y: 16 }, { kind: 'crate', x: 10, y: 16 }, { kind: 'barrel', x: 11, y: 16 },
      { kind: 'barrel', x: 22, y: 17 }, { kind: 'crate', x: 23, y: 17 }, { kind: 'crate', x: 26, y: 17 },
      { kind: 'rock', x: 27, y: 11 }, { kind: 'rock', x: 34, y: 13 }, { kind: 'rock', x: 33, y: 16 },
      { kind: 'festoon', x: 13, y: 21, w: 5, d: 1 },
    )
    p.scatter(decals(['seaweed', 'puddle'], 0.05, ['planks']))
    p.scatter(decals(['clover'], 0.05, ['grass']))
    p.npc({
      id: 'npc-sal', name: 'Sailor Sal', role: 'Sailor', look: look('sailorsal', 6, 14, 3, 'beanie'), speed: 1.3,
      stops: [{ x: 3, y: 18, wait: 12, say: -1 }, { x: 31, y: 18, wait: 12, emote: 'happy' }, { x: 16, y: 26, wait: 15, say: -1 }],
      lines: ['Fair winds today!', 'Knots? I know forty. Ask me one.', 'The lighthouse has never once been late.'],
    })
  },
}
